import {
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  FilePlus,
  FileText,
  Landmark,
  Loader2,
  LockKeyhole,
  MessageSquare,
  Paperclip,
  Pencil,
  Upload,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { useSelector } from "react-redux";

import { STATEMENT_FILE_ACCEPT } from "@/components/application/AppFileManager";
import { applicationPath } from "@/components/borrower/openApplication";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { extractionService } from "@/services/extraction";
import { ENTITY_TYPES } from "@/types/borrower";

import { choiceOptions, isDeclineOption } from "../../choices/wire";
import type { WireChoice } from "../../protocol/protocol";
import type { AgentState } from "../../store/agentSlice";
import { selectChoice } from "../../store/selectors";
import { AssistantCard, AssistantCardHeader } from "../../theme";
import type { NativeCard, NativeCardProps } from "..";
import { ReceiptLine, SectionLabel } from "../decisions/CardParts";
import { GenericApproval } from "../decisions/ChoiceParts";
import type { Overrides } from "../decisions/overrides";
import { useDecide } from "../decisions/useDecide";
import { useOnFake } from "../useOnFake";
import {
  dropFile,
  forgetHeld,
  holdFiles,
  setRead,
  uploadTo,
  useHeld,
  type Held,
  type HeldFile,
} from "./heldFiles";
import {
  addressOf,
  BUSINESS_ADDRESS,
  CARD_FIELDS,
  DBA,
  DRIVERS_LICENSE,
  HOME_ADDRESS,
  isNewApplicationCard,
  isNewApplicationReceipt,
  KEY_FIELDS,
  MORE_FIELDS,
  MORE_SUMMARY,
  NEW_APPLICATION,
  NOTE_FIELDS,
  RECEIPT_ROWS,
  refusedFields,
  type AddressGroup,
  type CardField,
  type FieldSource,
  type NewApplicationCardPayload,
  type NewApplicationReceipt,
} from "./payload";

type AgentRoot = { agent: AgentState };
type Tab = "upload" | "details" | "sendto";

/** As wide as the thread, edge to edge on a phone (the decision cards' width). */
const CARD = "w-full max-sm:-mx-3 max-sm:w-auto";

export const NO_COMPANY = "Add a company name to create the application.";
export const NO_FUNDER = "Pick at least one funder.";
export const FAKE_FILES = "Files aren't read or sent on the fake model.";
/** The card's override of the dropped PDF's signed values (`create_application.PDF_OVERRIDE`). */
const PDF_OVERRIDE = "application_pdf";
/** The application PDF the card reads on drop; any other PDF can be read from its row. */
const APPLICATION_NAME = /applic/i;

const SOURCE_CHIPS: Record<FieldSource, { icon: LucideIcon; label: string; className: string }> = {
  chat: { icon: MessageSquare, label: "chat", className: "bg-muted text-muted-foreground" },
  pdf: { icon: FileText, label: "PDF", className: "bg-primary/10 text-primary" },
  edited: {
    icon: Pencil,
    label: "edited",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  },
};

function SourceChip({ source }: { source: FieldSource }) {
  const { icon: Icon, label, className } = SOURCE_CHIPS[source];
  return (
    <span
      className={cn(
        "ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-px text-[0.65rem] font-medium normal-case",
        className
      )}
      data-source={source}
    >
      <Icon className="h-2.5 w-2.5" aria-hidden />
      {label}
    </span>
  );
}

const plural = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

// ---- the draft --------------------------------------------------------------------------------

interface Draft {
  /** The card (args_sha256) the draft started from: a redrawn card starts over. */
  sha: string | null;
  values: Record<string, string>;
  /** Typed on the card since it was drawn. */
  typed: string[];
  /** Filled from the dropped PDF since it was drawn. */
  fromPdf: string[];
  organizationIds: number[];
}

const initialDraft = (card: NewApplicationCardPayload, sha: string | null): Draft => ({
  sha,
  values: { ...card.values },
  typed: [],
  fromPdf: [],
  organizationIds: [...card.organization_ids],
});

const without = (names: string[], name: string) => names.filter((other) => other !== name);
const sameIds = (a: number[], b: number[]) =>
  JSON.stringify([...a].sort((x, y) => x - y)) === JSON.stringify([...b].sort((x, y) => x - y));

/**
 * The draft of `card`: the values as the user has them, each value's chip, and what a press
 * sends (`edits`). Autofill fills what the user has not typed; the user's own values win.
 */
function useDraft(card: NewApplicationCardPayload, choice: WireChoice, held: Held) {
  const sha = choice.args_sha256 ?? null;
  const [stored, setDraft] = useState<Draft>(() => initialDraft(card, sha));
  const draft = stored.sha === sha ? stored : initialDraft(card, sha);
  const change = (next: (current: Draft) => Draft) =>
    setDraft((prev) => next(prev.sha === sha ? prev : initialDraft(card, sha)));

  const source = (name: string): FieldSource | null => {
    if (!draft.values[name]) return null;
    if (draft.typed.includes(name)) return "edited";
    if (draft.fromPdf.includes(name)) return "pdf";
    return card.sources[name] ?? "chat";
  };

  const type = (name: string, value: string) =>
    change((current) => ({
      ...current,
      values: { ...current.values, [name]: value },
      typed:
        value === (card.values[name] ?? "")
          ? without(current.typed, name)
          : [...without(current.typed, name), name],
      fromPdf: without(current.fromPdf, name),
    }));

  /** The PDF's value for `name`, taken in place of the user's. */
  const takePdf = (name: string, value: string) =>
    change((current) => ({
      ...current,
      values: { ...current.values, [name]: value },
      typed: without(current.typed, name),
      fromPdf: [...without(current.fromPdf, name), name],
    }));

  /** The PDF's values, on every field the user has not made their own. */
  const autofill = (values: Record<string, string>) =>
    change((current) => {
      const filled = Object.entries(values).filter(
        ([name, value]) =>
          CARD_FIELDS.includes(name) &&
          value &&
          !current.typed.includes(name) &&
          card.sources[name] !== "edited"
      );
      return {
        ...current,
        values: { ...current.values, ...Object.fromEntries(filled) },
        fromPdf: [
          ...current.fromPdf.filter((name) => !filled.some(([other]) => other === name)),
          ...filled.map(([name]) => name),
        ],
      };
    });

  const toggleFunder = (id: number, on: boolean) =>
    change((current) => ({
      ...current,
      organizationIds: on
        ? [...current.organizationIds.filter((other) => other !== id), id]
        : current.organizationIds.filter((other) => other !== id),
    }));

  /** What Create sends: each changed value, the funders when changed, and the PDF's proof. */
  const edits = () => {
    const overrides: Overrides = {};
    for (const name of CARD_FIELDS) {
      const now = draft.values[name] ?? "";
      if (now !== (card.values[name] ?? "")) overrides[name] = now;
    }
    if (!sameIds(draft.organizationIds, card.organization_ids)) {
      overrides.organization_ids = draft.organizationIds;
    }
    // The PDF's proof, for a value it filled or one it gave that the form refused, or for one
    // typed before it was dropped (which the server then marks as a correction of the PDF's,
    // even with nothing else changed).
    const pdf = held.pdf?.values ?? {};
    const refused = held.pdf?.refused ?? [];
    const ofPdf = (name: string) => name in pdf || refused.includes(name);
    const typedBefore = Object.entries(card.sources).some(
      ([name, source]) => source === "edited" && ofPdf(name)
    );
    if (held.pdf && (typedBefore || Object.keys(overrides).some(ofPdf))) {
      overrides[PDF_OVERRIDE] = held.pdf.token;
    }
    return sha && Object.keys(overrides).length ? { overrides, shown: sha } : undefined;
  };

  return { draft, source, type, takePdf, autofill, toggleFunder, edits };
}

type DraftApi = ReturnType<typeof useDraft>;

// ---- Details ------------------------------------------------------------------------------------

/** The card's id prefix (`useId`), so two cards in one thread keep their fields apart. */
const FieldIds = createContext("na");
const useFieldId = (name: string) => `${useContext(FieldIds)}-${name}`;

function FieldHint({ children, error, id }: { children: ReactNode; error?: boolean; id?: string }) {
  return (
    <p id={id} className={cn("mt-1 text-xs", error ? "text-destructive" : "text-muted-foreground")}>
      {children}
    </p>
  );
}

/** A field's label, which names its input: the chip and the required star are part of it. */
function Label({
  field,
  label = field.label,
  more,
  source,
}: {
  field: CardField;
  label?: string;
  /** Said after the label, not shown (an address's "Street address"). */
  more?: string;
  source: FieldSource | null;
}) {
  return (
    <label
      htmlFor={useFieldId(field.name)}
      className="mb-1 flex items-center text-xs font-medium text-foreground"
    >
      {label}
      {/* Its own word in any engine, a block or not. */}
      {more && <span className="sr-only"> {more}</span>}
      {field.required && (
        <span className="ml-0.5 text-destructive" aria-hidden>
          *
        </span>
      )}
      {source && <SourceChip source={source} />}
    </label>
  );
}

function FieldInput({
  field,
  api,
  invalid,
  label,
  describedBy,
}: {
  field: CardField;
  api: DraftApi;
  invalid?: boolean;
  /** For an input without a `Label` of its own (an address's city, state and ZIP). */
  label?: string;
  /** The hint under it, when there is one. */
  describedBy?: string;
}) {
  const value = api.draft.values[field.name] ?? "";
  const id = useFieldId(field.name);
  const className = cn("h-8 text-sm", invalid && "border-destructive");
  const a11y = {
    id,
    "aria-label": label,
    "aria-describedby": describedBy,
    "aria-invalid": invalid || undefined,
    "aria-required": field.required || undefined,
  };
  if (field.select) {
    return (
      <select
        {...a11y}
        value={value}
        onChange={(event) => api.type(field.name, event.target.value)}
        className={cn(
          "flex w-full rounded-md border border-input bg-transparent px-2 shadow-sm",
          className
        )}
      >
        <option value="">{field.placeholder}</option>
        {[...new Set([...ENTITY_TYPES, ...(value ? [value] : [])])].map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>
    );
  }
  return (
    <Input
      {...a11y}
      value={value}
      placeholder={field.placeholder}
      onChange={(event) => api.type(field.name, event.target.value)}
      className={className}
    />
  );
}

/** What a value replaced, or that the user's own beats the PDF's (W4, W5); null for neither. */
function sourceHint(
  name: string,
  api: DraftApi,
  card: NewApplicationCardPayload,
  pdf: Record<string, string> | undefined
): ReactNode {
  const now = api.draft.values[name] ?? "";
  const was = card.values[name];
  const source = api.source(name);
  if (source === "pdf" && was && was !== now && card.sources[name] === "chat") {
    return (
      <>
        was <s>{was}</s> from chat
      </>
    );
  }
  const theirs = pdf?.[name];
  if (source === "edited" && theirs && theirs !== now) {
    return (
      <>
        Yours: autofill won't change it.{" "}
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={() => api.takePdf(name, theirs)}
        >
          Use the PDF's {theirs}
        </button>
      </>
    );
  }
  return null;
}

function Field({
  field,
  api,
  card,
  pdf,
  error,
}: {
  field: CardField;
  api: DraftApi;
  card: NewApplicationCardPayload;
  pdf?: Record<string, string>;
  error?: string | null;
}) {
  const hintId = useFieldId(`${field.name}-hint`);
  const hint = error || sourceHint(field.name, api, card, pdf);
  return (
    <div className={cn("min-w-0", field.wide && "sm:col-span-2")}>
      <Label field={field} source={api.source(field.name)} />
      <FieldInput
        field={field}
        api={api}
        invalid={!!error}
        describedBy={hint ? hintId : undefined}
      />
      {hint && (
        <FieldHint id={hintId} error={!!error}>
          {hint}
        </FieldHint>
      )}
    </div>
  );
}

function Address({
  group,
  api,
  errors,
}: {
  group: AddressGroup;
  api: DraftApi;
  errors: FieldErrors;
}) {
  const [street, ...rest] = group.fields;
  const source = group.fields.map((field) => api.source(field.name)).find(Boolean) ?? null;
  const error = group.fields.map((field) => errors[field.name]).find(Boolean);
  const hintId = useFieldId(`${street.name}-hint`);
  return (
    <div className="min-w-0 sm:col-span-2" role="group" aria-label={group.label}>
      <Label field={street} label={group.label} more={street.label} source={source} />
      <FieldInput
        field={street}
        api={api}
        invalid={!!errors[street.name]}
        describedBy={error ? hintId : undefined}
      />
      <div className="mt-1.5 grid grid-cols-[2fr_1fr_1fr] gap-1.5">
        {rest.map((field) => (
          <FieldInput
            key={field.name}
            field={field}
            api={api}
            invalid={!!errors[field.name]}
            label={`${group.label}: ${field.label}`}
            describedBy={error ? hintId : undefined}
          />
        ))}
      </div>
      {error && (
        <FieldHint id={hintId} error>
          {error}
        </FieldHint>
      )}
    </div>
  );
}

/** What is wrong with a field, by name: the card's own check, or the server's refusal of it. */
type FieldErrors = Record<string, string>;

function Details({
  api,
  card,
  pdf,
  errors,
}: {
  api: DraftApi;
  card: NewApplicationCardPayload;
  pdf?: Record<string, string>;
  errors: FieldErrors;
}) {
  const [opened, setMore] = useState(false);
  // A refused field under More fields is shown.
  const more = opened || MORE_FIELDS.some((field) => errors[field.name]);
  const filled = MORE_FIELDS.filter((field) => api.draft.values[field.name]).length;
  return (
    <div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {KEY_FIELDS.map((field) => (
          <Field
            key={field.name}
            field={field}
            api={api}
            card={card}
            pdf={pdf}
            error={errors[field.name]}
          />
        ))}
        <Address group={BUSINESS_ADDRESS} api={api} errors={errors} />
      </div>
      <button
        type="button"
        aria-expanded={more}
        onClick={() => setMore(!more)}
        className="mt-3 flex w-full min-w-0 items-center gap-1.5 border-t pt-2.5 text-left text-xs"
      >
        {more ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        <span className="shrink-0 whitespace-nowrap font-semibold">More fields</span>
        <span className="min-w-0 truncate text-muted-foreground">
          {filled ? `${filled} filled · ` : ""}
          {MORE_SUMMARY}
        </span>
      </button>
      {more && (
        <div className="mt-3 space-y-4">
          <section>
            <SectionLabel>Business</SectionLabel>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field field={DBA} api={api} card={card} pdf={pdf} error={errors[DBA.name]} />
            </div>
          </section>
          <section>
            <SectionLabel>Owner Information</SectionLabel>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                field={DRIVERS_LICENSE}
                api={api}
                card={card}
                pdf={pdf}
                error={errors[DRIVERS_LICENSE.name]}
              />
              <Address group={HOME_ADDRESS} api={api} errors={errors} />
            </div>
            <FieldHint>
              <LockKeyhole className="mr-1 inline h-3 w-3" aria-hidden />
              SSN is never taken here: add it on the app page.
            </FieldHint>
          </section>
          <section>
            <SectionLabel>Saved as a note</SectionLabel>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {NOTE_FIELDS.map((field) => (
                <Field
                  key={field.name}
                  field={field}
                  api={api}
                  card={card}
                  pdf={pdf}
                  error={errors[field.name]}
                />
              ))}
            </div>
            <FieldHint>
              Not application fields: saved on the app as a note, as the PDF upload does today.
            </FieldHint>
          </section>
        </div>
      )}
    </div>
  );
}

// ---- Upload -------------------------------------------------------------------------------------

function fileNote(file: HeldFile, filled: number, fake: boolean): ReactNode {
  if (fake) return "Held on the card";
  if (!file.application) return "Document · attached, read after create";
  if (file.read === "reading") return "Application · reading the application…";
  if (file.read === "failed") return "Application · couldn't read it: fill in Details by hand";
  return `Application · filled ${plural(filled, "field")} in Details`;
}

function FileRow({
  file,
  filled,
  fake,
  canRead,
  onRead,
  onDrop,
}: {
  file: HeldFile;
  filled: number;
  fake: boolean;
  canRead: boolean;
  onRead: () => void;
  onDrop: () => void;
}) {
  const Icon = file.application ? FileText : Landmark;
  return (
    <li className="flex items-center gap-2.5 rounded-md border px-2.5 py-2" data-testid="held-file">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{file.file.name}</p>
        <p
          className={cn(
            "flex items-center gap-1 text-xs text-muted-foreground",
            file.read === "read" && "text-green-700 dark:text-green-400",
            file.read === "failed" && "text-destructive"
          )}
        >
          {file.read === "reading" && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
          {file.read === "read" && <CircleCheck className="h-3 w-3" aria-hidden />}
          {fileNote(file, filled, fake)}
        </p>
      </div>
      {canRead && (
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={onRead}>
          Fill Details from it
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        aria-label={`Remove ${file.file.name}`}
        onClick={onDrop}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </li>
  );
}

const isPdf = (file: File) =>
  file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

function UploadTab({
  choiceRef,
  held,
  onRead,
  filled,
  fake,
}: {
  choiceRef: string;
  held: Held;
  /** Read `file` (held as the application) to fill Details. */
  onRead: (file: HeldFile) => void;
  filled: number;
  /** On the picked fake model: every file is only held, none read. */
  fake: boolean;
}) {
  const hasApplication = held.files.some((file) => file.application);
  const [refused, setRefused] = useState<string[]>([]);
  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      setRefused(rejected.map((rejection) => rejection.file.name));
      const first =
        hasApplication || fake
          ? -1
          : accepted.findIndex((file) => isPdf(file) && APPLICATION_NAME.test(file.name));
      const application = first >= 0 ? accepted[first] : null;
      if (application) onRead(holdFiles(choiceRef, [application], true)[0]);
      const rest = accepted.filter((file) => file !== application);
      if (rest.length) holdFiles(choiceRef, rest);
    },
    [choiceRef, hasApplication, fake, onRead]
  );
  // Only what upload-statements takes: one other file would fail every application's upload.
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: STATEMENT_FILE_ACCEPT,
    multiple: true,
  });
  const compact = held.files.length > 0;
  return (
    <div>
      {compact && (
        <ul className="mb-2 space-y-1.5">
          {held.files.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              filled={filled}
              fake={fake}
              canRead={!fake && !hasApplication && isPdf(file.file)}
              onRead={() => {
                dropFile(choiceRef, file.id);
                onRead(holdFiles(choiceRef, [file.file], true)[0]);
              }}
              onDrop={() => dropFile(choiceRef, file.id)}
            />
          ))}
        </ul>
      )}
      <div
        {...getRootProps()}
        className={cn(
          "flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed text-center transition-colors",
          compact ? "px-3 py-2.5" : "flex-col px-4 py-6",
          isDragActive
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
        )}
      >
        <input {...getInputProps()} aria-label="Add files" />
        <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
        <p className="text-sm font-medium">
          {compact
            ? "Drop more files or browse"
            : "Drop the application PDF, bank statements or other documents"}
        </p>
        {!compact && <p className="text-xs text-muted-foreground">or click to browse</p>}
      </div>
      {refused.length > 0 && (
        <FieldHint error>
          Not attached: {refused.join(", ")}. The card takes PDF, CSV, Excel, OFX, JPG and PNG
          files.
        </FieldHint>
      )}
      {fake ? (
        <FieldHint>{FAKE_FILES}</FieldHint>
      ) : (
        !compact && (
          <FieldHint>
            The application PDF fills in Details as soon as it's read. Statements and other
            documents are attached and read after you create, as on the portal.
          </FieldHint>
        )
      )}
    </div>
  );
}

// ---- Send to ------------------------------------------------------------------------------------

function SendTo({
  card,
  api,
  error,
}: {
  card: NewApplicationCardPayload;
  api: DraftApi;
  error: string | null;
}) {
  return (
    <div>
      <ul className={cn("divide-y rounded-md border", error && "border-destructive")}>
        {card.organizations.map((org) => {
          const on = api.draft.organizationIds.includes(org.id);
          return (
            <li key={org.id}>
              <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2.5 text-sm">
                <Checkbox
                  checked={on}
                  onCheckedChange={(checked) => api.toggleFunder(org.id, checked === true)}
                  aria-label={org.name}
                />
                {org.name}
              </label>
            </li>
          );
        })}
      </ul>
      {error && <FieldHint error>{error}</FieldHint>}
      <FieldHint>One application per funder, each with the same details and files.</FieldHint>
    </div>
  );
}

// ---- the waiting card ---------------------------------------------------------------------------

const tabId = (ids: string, tab: Tab) => `${ids}-tab-${tab}`;
const panelId = (ids: string) => `${ids}-panel`;

function TabButton({
  tab,
  ids,
  active,
  onSelect,
  children,
}: {
  tab: Tab;
  /** The card's id prefix (`useId`). */
  ids: string;
  active: boolean;
  onSelect: (tab: Tab) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={tabId(ids, tab)}
      aria-selected={active}
      aria-controls={panelId(ids)}
      tabIndex={active ? 0 : -1}
      onClick={() => onSelect(tab)}
      className={cn(
        "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

const Count = ({ n }: { n: number }) => (
  <span className="rounded-full bg-muted px-1.5 text-[0.65rem] font-semibold">{n}</span>
);
const Dot = () => (
  <span
    role="img"
    className="h-1.5 w-1.5 rounded-full bg-destructive"
    aria-label="Needs attention"
  />
);

function Header({ children, done }: { children?: ReactNode; done?: string }) {
  return (
    <AssistantCardHeader
      icon={done ? CircleCheck : FilePlus}
      tone={done ? "success" : "primary"}
      title={done ?? "New application"}
      className="border-b pb-3"
    >
      {children}
    </AssistantCardHeader>
  );
}

function Waiting({ card, choice }: { card: NewApplicationCardPayload; choice: WireChoice }) {
  const held = useHeld(choice.ref);
  const api = useDraft(card, choice, held);
  const { decide, pressed, error, redrawn } = useDecide(choice.ref, choice.args_sha256);
  const [tab, setTab] = useState<Tab>(Object.keys(card.values).length ? "details" : "upload");
  const [checked, setChecked] = useState(false);
  const several = card.organizations.length > 1;
  const creating = pressed !== null && !isDeclineOption(pressed);
  const reading = held.files.some((file) => file.read === "reading");

  // The server's refusal of a field marks it as the card's own checks do (W7v), until the user
  // changes it from what they pressed.
  const [sent, setSent] = useState<Draft | null>(null);
  const refused = refusedFields(error, card.labels).filter(({ name }) =>
    name === "organization_ids"
      ? sent && sameIds(sent.organizationIds, api.draft.organizationIds)
      : (sent?.values[name] ?? "") === (api.draft.values[name] ?? "")
  );
  const funderRefusal = refused.find(({ name }) => name === "organization_ids");
  const companyError = checked && !api.draft.values.company_name?.trim() ? NO_COMPANY : null;
  const funderError =
    checked && several && !api.draft.organizationIds.length
      ? NO_FUNDER
      : (funderRefusal?.message ?? null);
  const errors: FieldErrors = {
    ...Object.fromEntries(
      refused
        .filter(({ name }) => name !== "organization_ids")
        .map(({ name, message }) => [name, message])
    ),
    ...(companyError && { company_name: companyError }),
  };
  const detailsError = Object.keys(errors).length > 0;

  // A read that comes back after a redraw fills the draft the card has then.
  const autofill = useRef(api.autofill);
  useEffect(() => {
    autofill.current = api.autofill;
  });
  const fake = useOnFake(card, choice.thread_id);
  const read = useCallback(
    (file: HeldFile) => {
      if (fake) return; // the parse is a paid model's
      setRead(choice.ref, file.id, "reading");
      extractionService.parseApplicationForCard(file.file).then(
        (pdf) => {
          // A file dropped while it was read fills nothing.
          if (setRead(choice.ref, file.id, "read", pdf)) autofill.current(pdf.values);
        },
        () => setRead(choice.ref, file.id, "failed")
      );
    },
    [choice.ref, fake]
  );
  // The PDF's values the card still has, as autofilled or as the redrawn card marks them.
  // An address is one field (one chip), as the design counts it.
  const filled = held.pdf
    ? new Set(
        Object.keys(held.pdf.values)
          .filter((name) => api.source(name) === "pdf")
          .map((name) => addressOf(name)?.label ?? name)
      ).size
    : 0;

  const ids = useId();
  const tabs: Tab[] = several ? ["upload", "details", "sendto"] : ["upload", "details"];
  /** Arrow keys, Home and End move between the tabs (the WAI tabs pattern). */
  const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.indexOf(tab);
    const to = {
      ArrowRight: (at + 1) % tabs.length,
      ArrowLeft: (at - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    }[event.key];
    if (to === undefined) return;
    event.preventDefault();
    setTab(tabs[to]);
    document.getElementById(tabId(ids, tabs[to]))?.focus();
  };

  const create = () => {
    setChecked(true);
    const missingCompany = !api.draft.values.company_name?.trim();
    const missingFunder = several && !api.draft.organizationIds.length;
    if (missingCompany || missingFunder) {
      setTab(missingCompany ? "details" : "sendto");
      return;
    }
    setSent(api.draft);
    void decide("approve", api.edits());
  };

  const funders = card.organizations.filter((org) => api.draft.organizationIds.includes(org.id));
  const who = funders.length ? (
    <>
      For{" "}
      {funders.map((org, index) => (
        <span key={org.id}>
          {index > 0 && " and "}
          <b className="font-semibold text-foreground">{org.name}</b>
        </span>
      ))}
      {funders.length > 1 && ` · ${plural(funders.length, "application")}`}
    </>
  ) : several ? (
    "Pick a funder in Send to"
  ) : null;
  const missing = [companyError && "add a company name", funderError && "pick a funder"].filter(
    Boolean
  );
  const options = choiceOptions(choice);
  const approve = options.find((option) => !isDeclineOption(option.id));
  const deny = options.find((option) => isDeclineOption(option.id));

  return (
    <AssistantCard className={CARD} data-testid="new-application" data-state="pending">
      <Header>
        <Badge variant="secondary">Draft · not created</Badge>
      </Header>
      <div role="tablist" className="flex border-b px-1.5" onKeyDown={onTabKey}>
        <TabButton tab="upload" ids={ids} active={tab === "upload"} onSelect={setTab}>
          Upload
          {held.files.length > 0 && <Count n={held.files.length} />}
        </TabButton>
        <TabButton tab="details" ids={ids} active={tab === "details"} onSelect={setTab}>
          Details
          {reading ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-label="Reading the application" />
          ) : (
            detailsError && <Dot />
          )}
        </TabButton>
        {several && (
          <TabButton tab="sendto" ids={ids} active={tab === "sendto"} onSelect={setTab}>
            Send to
            {funderError ? (
              <Dot />
            ) : (
              api.draft.organizationIds.length > 0 && <Count n={api.draft.organizationIds.length} />
            )}
          </TabButton>
        )}
      </div>
      <fieldset
        role="tabpanel"
        id={panelId(ids)}
        aria-labelledby={tabId(ids, tab)}
        disabled={creating}
        className={cn("min-w-0 px-3.5 py-3", creating && "pointer-events-none opacity-60")}
      >
        {tab === "upload" && (
          <UploadTab choiceRef={choice.ref} held={held} onRead={read} filled={filled} fake={fake} />
        )}
        {tab === "details" && (
          <FieldIds.Provider value={ids}>
            <Details api={api} card={card} pdf={held.pdf?.values} errors={errors} />
          </FieldIds.Provider>
        )}
        {tab === "sendto" && several && <SendTo card={card} api={api} error={funderError} />}
      </fieldset>
      <div className="flex flex-wrap items-center gap-2 border-t px-3.5 py-2.5">
        <span className="min-w-0 flex-1 text-xs text-muted-foreground">{who}</span>
        <div className="flex shrink-0 gap-2">
          {deny && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={pressed !== null}
              onClick={() => void decide(deny.id)}
            >
              Discard
            </Button>
          )}
          {approve && (
            <Button type="button" size="sm" disabled={pressed !== null || reading} onClick={create}>
              {(creating || reading) && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              )}
              {creating ? "Creating…" : reading ? "Reading the application…" : "Create"}
            </Button>
          )}
        </div>
        {missing.length > 0 && (
          <p role="alert" className="flex w-full items-center gap-1.5 text-xs text-destructive">
            <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {`${missing.join(" and ").replace(/^./, (c) => c.toUpperCase())} to create.`}
          </p>
        )}
        {error && (
          <p role="alert" className="w-full text-xs text-destructive">
            {error}
          </p>
        )}
        {redrawn && (
          <p role="status" className="w-full text-xs font-medium text-primary">
            {redrawn}
          </p>
        )}
      </div>
    </AssistantCard>
  );
}

// ---- after the press ----------------------------------------------------------------------------

/** A card that no longer waits and has no receipt: creating, discarded, closed or failed. */
function Settled({ choice, failed }: { choice: WireChoice; failed: string | null }) {
  const creating =
    !failed && choice.status === "decided" && !isDeclineOption(choice.chosen) && !choice.error;
  // Declined, expired, superseded or failed: nothing will take the held files.
  const closed =
    choice.status === "expired" ||
    choice.status === "cancelled" ||
    (choice.status === "decided" && (!choice.chosen || isDeclineOption(choice.chosen))) ||
    !!(failed || choice.error);
  useEffect(() => {
    if (closed) forgetHeld(choice.ref);
  }, [closed, choice.ref]);
  return (
    <AssistantCard className={CARD} data-testid="new-application" data-state={choice.status}>
      <Header />
      <div className="px-3.5 py-2.5">
        {failed || choice.error ? (
          <div className="space-y-0.5 text-xs text-destructive">
            {failed && <p className="font-medium">{failed}</p>}
            {choice.error && choice.error !== failed && <p>{choice.error}</p>}
          </div>
        ) : creating ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Creating…
          </p>
        ) : (
          <ReceiptLine choice={choice} done="Created" notDone="Not created" />
        )}
      </div>
    </AssistantCard>
  );
}

function UploadLine({ state, files }: { state: Held["uploads"][number]; files: number }) {
  if (state === "uploading") {
    return (
      <span className="text-muted-foreground">
        <Loader2 className="mr-1 inline h-3 w-3 animate-spin" aria-hidden />
        Attaching {plural(files, "file")}…
      </span>
    );
  }
  if (state === "done") {
    return <span className="text-muted-foreground">{plural(files, "file")} attached</span>;
  }
  return null;
}

function Receipt({
  receipt,
  choice,
  choiceRef,
}: {
  receipt: NewApplicationReceipt;
  choice: WireChoice | null;
  choiceRef: string | null;
}) {
  const held = useHeld(choiceRef);
  const files = held.files.length || held.attached;
  const { applications, values } = receipt;
  const ids = applications.map((app) => app.id);
  // On the picked fake model nothing is sent (the server refuses its create: never a receipt).
  const fake = useOnFake(
    isNewApplicationCard(choice?.card) ? choice.card : null,
    choice?.thread_id
  );
  useEffect(() => {
    if (!choiceRef || fake) return;
    const all = applications.map((app) => app.id);
    for (const id of all) void uploadTo(choiceRef, id, all);
  }, [choiceRef, applications, fake]);

  // By name: the receipt carries no note value (an older one, its values).
  const noted = NOTE_FIELDS.filter((field) =>
    receipt.noted ? receipt.noted.includes(field.name) : values[field.name]
  ).map((field) => field.label);
  const title =
    applications.length > 1 ? `Created ${applications.length} applications` : "Application created";
  const failed = applications.filter((app) => held.uploads[app.id] === "failed");
  const done = applications.filter((app) => held.uploads[app.id] === "done").length;
  // Every upload has come back, some perhaps failed: the hint says where the files went.
  const settled = files > 0 && done + failed.length === applications.length;
  const attachHint = !files
    ? ""
    : fake
      ? `${FAKE_FILES} `
      : !settled
        ? `Attaching ${plural(files, "file")}. `
        : !done
          ? `Couldn't attach ${plural(files, "file")}. `
          : failed.length
            ? `Attached ${plural(files, "file")} to ${done} of ${applications.length} applications. `
            : `Attached ${plural(files, "file")}. `;
  return (
    <AssistantCard className={CARD} data-testid="new-application" data-state="created">
      <Header done={title}>
        <Badge className="border-transparent bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300">
          Created
        </Badge>
      </Header>
      <div className="px-3.5 py-3">
        {/* Only what was given; dense, so a short value fills the gap a wide one leaves. */}
        <dl className="grid grid-flow-row-dense grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
          {RECEIPT_ROWS.filter((row) => values[row.name]).map((row) => (
            <div key={row.name} className={cn("min-w-0", row.wide && "col-span-2")}>
              <dt className="text-xs text-muted-foreground">{row.label}</dt>
              <dd className="truncate font-medium">{values[row.name]}</dd>
            </div>
          ))}
        </dl>
        {(files > 0 || noted.length > 0) && (
          <FieldHint>
            <Paperclip className="mr-1 inline h-3 w-3" aria-hidden />
            {attachHint}
            {noted.length > 0 &&
              `${noted.join(", ")} ${noted.length > 1 ? "are" : "is"} in a note.`}
          </FieldHint>
        )}
      </div>
      <ul className="space-y-1.5 border-t px-3.5 py-2.5 text-xs">
        {applications.map((app) => (
          <li key={app.id} className="flex flex-wrap items-center gap-x-1.5" data-testid="created">
            <CircleCheck
              className="h-3.5 w-3.5 shrink-0 text-green-700 dark:text-green-400"
              aria-hidden
            />
            <span className="min-w-0 flex-1">
              Created <b className="font-semibold">{app.company_name}</b> for {app.funder} ·{" "}
              <a
                href={applicationPath(app.id)}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-primary hover:underline"
              >
                Open application {app.id}
              </a>
            </span>
            {files > 0 && <UploadLine state={held.uploads[app.id]} files={files} />}
          </li>
        ))}
        {failed.map((app) => (
          <li
            key={`failed-${app.id}`}
            role="alert"
            className="flex flex-wrap items-center gap-1.5 text-destructive"
          >
            <CircleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1">
              Couldn't attach the files to {app.funder}'s application.{" "}
              {fake ? "Upload them on the app page." : "Try again, or upload them on the app page."}
            </span>
            {/* On the picked fake a retry would post the files and start a paid intake. */}
            {!fake && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-6 px-2 text-xs"
                onClick={() => choiceRef && void uploadTo(choiceRef, app.id, ids, true)}
              >
                Try again
              </Button>
            )}
          </li>
        ))}
        <li className="flex items-center gap-1.5 text-muted-foreground">
          <Loader2 className={cn("h-3.5 w-3.5 shrink-0", done > 0 && "animate-spin")} aria-hidden />
          {done > 0
            ? `Intake running · reading ${plural(files, "document")}`
            : "Intake reads the documents uploaded on the app page."}
        </li>
        <li className="flex items-center gap-1.5 text-muted-foreground">
          <LockKeyhole className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Add the owner's SSN on the app page.
        </li>
      </ul>
      {choice && (
        <div className="border-t px-3.5 py-2.5">
          <ReceiptLine choice={choice} done="Created" notDone="Not created" />
        </div>
      )}
    </AssistantCard>
  );
}

/** create_application on the web: the New application card, then its receipt. */
function NewApplicationCard({ artifact }: NativeCardProps) {
  const choice = useSelector((state: AgentRoot) =>
    artifact.choiceRef ? selectChoice(state, artifact.choiceRef) : null
  );
  const receipt = isNewApplicationReceipt(artifact.resultCard) ? artifact.resultCard : null;
  if (receipt && artifact.state === "success") {
    return <Receipt receipt={receipt} choice={choice} choiceRef={artifact.choiceRef} />;
  }
  const card = isNewApplicationCard(choice?.card) ? choice.card : null;
  if (!choice || !card) return <GenericApproval artifact={artifact} />;
  if (choice.status === "pending") return <Waiting card={card} choice={choice} />;
  return <Settled choice={choice} failed={artifact.state === "failed" ? artifact.line : null} />;
}

export const NEW_APPLICATION_CARD: NativeCard = {
  Card: NewApplicationCard,
  // A call with neither card (an older message, one refused before it drew one) keeps its line.
  shows: (artifact) =>
    isNewApplicationReceipt(artifact.resultCard) || artifact.choiceCardKind === NEW_APPLICATION,
};
